import { FieldHandler, useField } from '@kizi/comparator-core';
import { Div, DivProps, InputProps, P, Span } from '@kizi/core-components';
import { IconV2 as Icon, IconV2Props } from '@kizi/shared-components';
import React, { useState } from 'react';
import { BaseWithFormProps, WithFormProps } from './HOCs/withFormProps';
import { Spinner } from './Spinner';
import { NoSuccessIconBasicInput } from './wrappers/inputs/withFormProps';

export type ComponentVariation = 'default' | 'sidenav';

export type AddressInputProps = WithFormProps<
  React.ComponentPropsWithRef<typeof NoSuccessIconBasicInput>
>;
export interface AddressProps extends DivProps {
  addition?: AddressInputProps;
  city?: string;
  houseNumber?: AddressInputProps;
  iconProps?: IconV2Props;
  pTextVariant?: string;
  postcode?: AddressInputProps;
  street?: string;
  variation?: ComponentVariation;
  loading?: boolean;
}

export const Address: React.FC<AddressProps> = ({
  addition,
  city,
  houseNumber,
  iconProps = { name: 'home', width: 21, height: 21 },
  pTextVariant = 'footnoteRegular',
  postcode,
  street,
  variation = 'default',
  loading,
  ...divProps
}) => {
  const variant = variation !== 'default' ? 'addressInSidenav' : '';
  const updatedPostcodeProps: any = {
    ...postcode,
    'type': (postcode?.value as string)?.length >= 4 ? 'text' : 'tel',
    'inputMode': (postcode?.value as string)?.length >= 4 ? 'text' : 'numeric',
    'pattern': (postcode?.value as string)?.length >= 4 ? undefined : '[0-9]*',
  };

  return (
    <Div {...divProps} variant={variant}>
      <Div display="grid" gridAutoFlow="column" gridGap={3}>
        <NoSuccessIconBasicInput {...updatedPostcodeProps} />
        <NoSuccessIconBasicInput {...houseNumber} />
        <NoSuccessIconBasicInput {...addition} />
      </Div>
      {street && city && (
        <Div alignItems="center" display="flex" mt={2}>
          <Icon variant="regular" {...iconProps} />
          <P ml={2} variant={pTextVariant}>
            {street}, {city}
          </P>
        </Div>
      )}
      {loading && (
        <Span alignItems="center" display="flex" mt={2}>
          <Spinner />
          <P ml={2} variant="footnoteRegular">
            Laden...
          </P>
        </Span>
      )}
    </Div>
  );
};

export interface AddressWrapperProps extends DivProps {
  city: {
    fieldName: string;
    props?: BaseWithFormProps & InputProps;
  };
  street: {
    fieldName: string;
    props?: BaseWithFormProps & InputProps;
  };
  iconProps?: IconV2Props;
  addition: {
    fieldName: string;
    props?: BaseWithFormProps & InputProps;
  };
  houseNumber: {
    fieldName: string;
    props?: BaseWithFormProps & InputProps;
  };
  postcode: {
    fieldName: string;
    props?: BaseWithFormProps & InputProps;
  };
  variation?: ComponentVariation;
}

const getPropsFromFormField = (formField: FieldHandler) => ({
  _error: formField.errors ? formField.errors.join('\n') : undefined,
  name: formField.name,
  value: formField.value,
  onChange: formField.onChange,
});

export const AddressWrapper: React.FC<AddressWrapperProps> = ({
  city,
  iconProps = { name: 'home' },
  street,
  postcode,
  houseNumber,
  addition,
  variation = 'default',
  ...divProps
}) => {
  const postcodeField = useField(postcode.fieldName);
  const housenumberField = useField(houseNumber.fieldName);
  const additionField = useField(addition.fieldName);
  const cityField = useField(city.fieldName);
  const streetField = useField(street.fieldName);

  const [postcodeError, setPostcodeError] = useState(
    postcodeField?.errors ? postcodeField.errors.join('\n') : undefined,
  );

  if (
    !cityField ||
    !streetField ||
    !postcodeField ||
    !housenumberField ||
    !additionField
  )
    return null;
  const postcodeProps = {
    ...getPropsFromFormField(postcodeField),
    ...postcode.props,
    onBlur: (e) => {
      setPostcodeError(
        postcodeField.errors ? postcodeField.errors.join('\n') : undefined,
      );
      if (postcode.props?.onBlur) postcode.props?.onBlur(e);
    },
    _error: postcodeError,
  };
  const housenumberProps = {
    ...getPropsFromFormField(housenumberField),
    ...houseNumber.props,
  };
  const additionProps = {
    ...getPropsFromFormField(additionField),
    ...addition.props,
  };
  return (
    <Address
      {...divProps}
      variation={variation}
      iconProps={iconProps}
      city={cityField.value as string}
      street={streetField.value as string}
      postcode={postcodeProps}
      houseNumber={housenumberProps}
      addition={additionProps}
    />
  );
};
